// Tab „Aktivität“ (Handoff Sonstige Kontakte §2, Screen 07). Logik wie bisher in KontaktAktivitaet:
// Schnell loggen (Anruf/Mail/Meeting/Notiz), Suche, Filter-Chips aus AKTIVITAET_FILTER, Timeline nach Tag,
// E-Mails aufklappbar (bereinigtes HTML), Objekt-Ziele klickbar.
import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  ArrowUpRight, CaretDown, CaretRight, CalendarBlank, CircleNotch, ClockCounterClockwise, EnvelopeSimple, FileText, MagnifyingGlass,
  Note, Phone, Pulse, Receipt, Ticket, Users, X, type Icon,
} from '@phosphor-icons/react';
import { MeldungDialog } from '../../../../components/ui/MeldungDialog';
import { supabase } from '../../../../lib/supabase/client';
import { ladeAktivitaeten } from '../../../../lib/kontakte/api';
import { meldeKontaktGeaendert } from '../../../../lib/kontakte/detailTab';
import { baueSchnellLog, betreffPlatzhalter, speichereSchnellLog, type Richtung, type SchnellLogTyp } from '../../../../lib/kontakte/schnellLog';
import { AKTIVITAET_FILTER, AKTIVITAET_TYP_LABEL, filterAktivitaeten, gruppiereNachTag } from '../../../../lib/kontakte/timeline';
import type { Aktivitaet, AktivitaetTyp } from '../../../../lib/kontakte/types';
import { FarbIcon, LeerKarte, TabLaedt } from './teile';

/** Icon und Palettenfarbe je Typ (Prototyp TYP). */
const TYP: Record<AktivitaetTyp, { icon: Icon; farbe: number }> = {
  notiz: { icon: Note, farbe: 3 },
  anruf: { icon: Phone, farbe: 2 },
  email: { icon: EnvelopeSimple, farbe: 0 },
  meeting: { icon: Users, farbe: 1 },
  aenderung: { icon: ClockCounterClockwise, farbe: 5 },
  ticket: { icon: Ticket, farbe: 4 },
  termin: { icon: CalendarBlank, farbe: 3 },
  dokument: { icon: FileText, farbe: 5 },
  rechnung: { icon: Receipt, farbe: 4 },
};

const LOG_KNOEPFE: readonly { key: SchnellLogTyp; label: string; icon: Icon; farbe: number }[] = [
  { key: 'anruf', label: 'Anruf', icon: Phone, farbe: 2 },
  { key: 'mail', label: 'Mail', icon: EnvelopeSimple, farbe: 0 },
  { key: 'meeting', label: 'Meeting', icon: Users, farbe: 1 },
  { key: 'notiz', label: 'Notiz', icon: Note, farbe: 3 },
];

function uhrzeit(datum: string): string {
  const d = new Date(datum);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// E-Mail-HTML gegen die üblichen Vektoren bereinigen; <style>/<link> raus (kein seitenweiter
// CSS-Ausbruch), Inline-Styles bleiben. cid:-Bilder laden hier evtl. nicht (Graph-Abruf fehlt).
function saubereHtml(html: string): string {
  return DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'style', 'link'],
    FORBID_ATTR: ['onerror', 'onload'],
  });
}

const FELD = { border: 'none', borderRadius: 14, background: 'var(--sk-field)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 13.5, color: 'var(--ink)', outline: 'none' } as const;

export function SonstigeAktivitaet({ id, onNavigateToObjekt }: { id: string; onNavigateToObjekt: (objektId: string) => void }) {
  const [aktivitaeten, setAktivitaeten] = useState<Aktivitaet[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [typFilter, setTypFilter] = useState('alle');
  const [suche, setSuche] = useState('');
  const [offenId, setOffenId] = useState<string | null>(null);
  const [htmlCache, setHtmlCache] = useState<Record<string, { html?: string; text?: string; loading: boolean }>>({});
  const [meldung, setMeldung] = useState<string | null>(null);

  // Schnell-Logging
  const [logTyp, setLogTyp] = useState<SchnellLogTyp | null>(null);
  const [betreff, setBetreff] = useState('');
  const [logNotiz, setLogNotiz] = useState('');
  const [richtung, setRichtung] = useState<Richtung>('ausgehend');
  const [dauer, setDauer] = useState('');
  const [speichern, setSpeichern] = useState(false);

  const laden = () => {
    setLaedt(true);
    ladeAktivitaeten('kontakt', id).then((d) => { setAktivitaeten(d); setLaedt(false); });
  };
  useEffect(() => { laden(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const logOeffnen = (t: SchnellLogTyp) => {
    setLogTyp((v) => (v === t ? null : t));
    setBetreff(''); setLogNotiz(''); setRichtung('ausgehend'); setDauer('');
  };
  const logSpeichern = async () => {
    if (!logTyp) return;
    const ziel = baueSchnellLog({ typ: logTyp, betreff, notiz: logNotiz, richtung, dauer });
    if (!ziel) return;
    setSpeichern(true);
    try {
      await speichereSchnellLog('kontakt', id, ziel);
      setLogTyp(null);
      laden();
      meldeKontaktGeaendert();
    } catch (err: unknown) {
      setMeldung('Speichern fehlgeschlagen: ' + ((err as { message?: string } | null)?.message || 'Unbekannter Fehler'));
    } finally {
      setSpeichern(false);
    }
  };

  const gefiltert = useMemo(() => filterAktivitaeten(aktivitaeten, typFilter, suche), [aktivitaeten, typFilter, suche]);
  const gruppen = useMemo(() => gruppiereNachTag(gefiltert, new Date()), [gefiltert]);

  const emailOeffnen = async (a: Aktivitaet) => {
    if (a.typ !== 'email' || !a.id) return;
    const eid = a.id;
    if (offenId === eid) { setOffenId(null); return; }
    setOffenId(eid);
    if (!htmlCache[eid]) {
      setHtmlCache((c) => ({ ...c, [eid]: { loading: true } }));
      const { data } = await supabase.from('emails').select('html_inhalt, text_inhalt').eq('id', eid).maybeSingle();
      const zeile = data as { html_inhalt: string | null; text_inhalt: string | null } | null;
      setHtmlCache((c) => ({ ...c, [eid]: { loading: false, html: zeile?.html_inhalt ?? undefined, text: zeile?.text_inhalt ?? undefined } }));
    }
  };

  const mitRichtung = logTyp === 'anruf' || logTyp === 'mail';
  const mitDauer = logTyp === 'anruf' || logTyp === 'meeting';

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      {/* Schnell loggen */}
      <section aria-label="Schnell loggen" className="sk-glas sk-rise flex flex-col" style={{ borderRadius: 24, padding: 14, gap: 12 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)', marginRight: 4 }}>Schnell loggen</span>
          {LOG_KNOEPFE.map((b) => {
            const an = logTyp === b.key;
            return (
              <button key={b.key} type="button" aria-pressed={an} onClick={() => logOeffnen(b.key)}
                data-tip={`${b.label} – ${b.key === 'notiz' ? 'Notiz zum Kontakt speichern' : `${b.label} protokollieren`}`}
                className="sk-log sk-tipp flex cursor-pointer items-center"
                style={{
                  height: 36, padding: '0 13px 0 6px', border: 'none', borderRadius: 18, gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--ink)',
                  background: an ? `color-mix(in srgb, var(--sk-av-${b.farbe}-b) 18%, transparent)` : 'var(--fill)',
                  boxShadow: an ? `inset 0 0 0 1.5px var(--sk-av-${b.farbe}-b)` : 'none',
                }}>
                <FarbIcon icon={b.icon} farbe={b.farbe} groesse={26} radius={13} iconGroesse={13} />
                {b.label}
              </button>
            );
          })}
        </div>

        {logTyp && (
          <div className="sk-rise flex flex-col" style={{ gap: 8, animationDuration: '.3s' }}>
            {logTyp === 'notiz' ? (
              <textarea value={betreff} onChange={(e) => setBetreff(e.target.value)} rows={3} placeholder="Notiz …" aria-label="Notiz"
                className="w-full" style={{ ...FELD, minHeight: 80, resize: 'vertical', padding: 12 }} />
            ) : (
              <>
                <input value={betreff} onChange={(e) => setBetreff(e.target.value)} placeholder={betreffPlatzhalter(logTyp)} aria-label="Betreff"
                  className="w-full" style={{ ...FELD, height: 42, padding: '0 12px' }} />
                <div className="flex flex-wrap" style={{ gap: 8 }}>
                  {mitRichtung && (
                    <div role="radiogroup" aria-label="Richtung" className="flex" style={{ gap: 3, padding: 3, borderRadius: 13, background: 'var(--fill2)' }}>
                      {(['ausgehend', 'eingehend'] as const).map((r) => {
                        const an = richtung === r;
                        return (
                          <button key={r} type="button" role="radio" aria-checked={an} onClick={() => setRichtung(r)} className="sk-tipp cursor-pointer"
                            style={{ height: 30, padding: '0 12px', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, color: 'var(--ink)', background: an ? 'var(--bg)' : 'transparent', boxShadow: an ? '0 2px 8px -3px var(--shade)' : 'none' }}>
                            {r === 'ausgehend' ? 'Ausgehend' : 'Eingehend'}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {mitDauer && (
                    <label className="flex items-center" style={{ height: 36, padding: '0 12px', borderRadius: 13, gap: 6, background: 'var(--fill2)' }}>
                      <input type="number" min="0" value={dauer} onChange={(e) => setDauer(e.target.value)} placeholder="Dauer" aria-label="Dauer in Minuten"
                        className="border-none bg-transparent outline-none" style={{ width: 56, fontSize: 13, fontWeight: 700, color: 'var(--ink)' }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Min.</span>
                    </label>
                  )}
                </div>
                <textarea value={logNotiz} onChange={(e) => setLogNotiz(e.target.value)} rows={2} placeholder="Notiz (optional) …" aria-label="Notiz (optional)"
                  className="w-full" style={{ ...FELD, resize: 'vertical', padding: 12 }} />
              </>
            )}
            <div className="flex justify-end" style={{ gap: 8 }}>
              <button type="button" onClick={() => setLogTyp(null)} className="sk-nav sk-tipp flex cursor-pointer items-center"
                style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 18, gap: 5, fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)', background: 'transparent' }}>
                <X weight="bold" size={12} />Abbrechen
              </button>
              <button type="button" onClick={() => void logSpeichern()} disabled={speichern || !betreff.trim()}
                className="sk-tipp flex cursor-pointer items-center disabled:cursor-not-allowed"
                style={{ height: 36, padding: '0 18px', border: 'none', borderRadius: 18, gap: 6, fontSize: 12.5, fontWeight: 800, color: 'var(--acc-ink)', background: 'var(--acc-fill)', opacity: speichern || !betreff.trim() ? 0.4 : 1 }}>
                {speichern && <CircleNotch size={12} className="animate-spin" />}Speichern
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Suche */}
      <label className="sk-glas sk-suche flex items-center" style={{ height: 42, padding: '0 14px', borderRadius: 21, gap: 10 }}>
        <MagnifyingGlass weight="bold" size={15} className="shrink-0" />
        <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Timeline durchsuchen …" aria-label="Timeline durchsuchen"
          className="min-w-0 flex-1 border-none bg-transparent outline-none" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }} />
      </label>

      {/* Filter-Chips */}
      <div role="group" aria-label="Filter" className="flex flex-wrap" style={{ gap: 6 }}>
        {AKTIVITAET_FILTER.map((f) => {
          const an = typFilter === f.key;
          const n = f.key === 'alle' ? aktivitaeten.length : aktivitaeten.filter((a) => a.typ === f.key).length;
          return (
            <button key={f.key} type="button" aria-pressed={an} onClick={() => setTypFilter(f.key)}
              className={`sk-tipp flex cursor-pointer items-center ${an ? '' : 'sk-chip'}`}
              style={{
                height: 30, padding: '0 12px', border: 'none', borderRadius: 15, gap: 5, fontSize: 12, fontWeight: 700,
                ...(an ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: '0 4px 10px -4px var(--shade)' } : { color: 'var(--ink)' }),
              }}>
              {f.label}{n > 0 && <span style={{ opacity: 0.6 }}>{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {laedt ? (
        <TabLaedt />
      ) : gruppen.length === 0 ? (
        <LeerKarte icon={Pulse} text={aktivitaeten.length === 0 ? 'Keine Aktivität' : 'Nichts gefunden'} />
      ) : (
        <div className="flex flex-col" style={{ gap: 16 }}>
          {gruppen.map((g) => (
            <div key={g.key} className="flex flex-col" style={{ gap: 6 }}>
              <h3 className="m-0" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)', paddingLeft: 6 }}>{g.label}</h3>
              {g.items.map((a, i) => {
                const istEmail = a.typ === 'email' && a.expandierbar === true && !!a.id;
                const istObjektKlick = a.ziel?.typ === 'objekt';
                const offen = istEmail && offenId === a.id;
                const eintrag = htmlCache[a.id ?? ''];
                const t = TYP[a.typ];
                const klick = istEmail ? () => void emailOeffnen(a) : istObjektKlick && a.ziel ? () => onNavigateToObjekt(a.ziel!.id) : undefined;
                return (
                  <div key={`${a.typ}-${a.id ?? i}-${i}`} className="sk-glas sk-rise overflow-hidden" style={{ borderRadius: 18, animationDuration: '.4s' }}>
                    <div
                      role={klick ? 'button' : undefined}
                      tabIndex={klick ? 0 : undefined}
                      aria-expanded={istEmail ? offen : undefined}
                      onClick={klick}
                      onKeyDown={klick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); klick(); } } : undefined}
                      className={`flex items-center ${klick ? 'sk-nav cursor-pointer' : ''}`}
                      style={{ padding: '11px 12px', gap: 12 }}
                    >
                      <FarbIcon icon={t.icon} farbe={t.farbe} groesse={34} radius={11} iconGroesse={16} />
                      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
                        <div className="flex min-w-0 items-center" style={{ gap: 7 }}>
                          <span className="shrink-0" style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: `var(--sk-av-${t.farbe}-b)` }}>{AKTIVITAET_TYP_LABEL[a.typ]}</span>
                          {a.detail && <span className="truncate" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{a.detail}</span>}
                        </div>
                        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{a.titel}</span>
                      </div>
                      <span className="immo-zahl shrink-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{uhrzeit(a.datum)}</span>
                      {istEmail && (offen ? <CaretDown weight="bold" size={14} color="var(--acc)" /> : <CaretRight weight="bold" size={14} color="var(--ink3)" />)}
                      {!istEmail && istObjektKlick && <ArrowUpRight weight="bold" size={13} color="var(--ink3)" />}
                    </div>
                    {offen && (
                      <div style={{ borderTop: '1px solid var(--line)', padding: 12 }}>
                        <div style={{ borderRadius: 14, padding: 14, background: 'var(--sk-mail-bg)', color: 'var(--sk-mail-ink)' }}>
                          {eintrag?.loading ? (
                            <div className="flex justify-center" style={{ padding: 20 }}><CircleNotch size={18} className="animate-spin" /></div>
                          ) : eintrag?.html ? (
                            <div className="max-h-[440px] overflow-y-auto break-words [&_img]:max-w-full [&_a]:underline [&_table]:max-w-full"
                              style={{ fontSize: 13, lineHeight: 1.55 }}
                              dangerouslySetInnerHTML={{ __html: saubereHtml(eintrag.html) }} />
                          ) : eintrag?.text ? (
                            <pre className="m-0 max-h-[440px] overflow-y-auto whitespace-pre-wrap break-words" style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.55 }}>{eintrag.text}</pre>
                          ) : (
                            <p className="m-0" style={{ fontSize: 12, opacity: 0.6 }}>Kein Inhalt vorhanden.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {meldung && <MeldungDialog fehler text={meldung} onSchliessen={() => setMeldung(null)} />}
    </div>
  );
}
